import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    IconButton,
    Chip,
    Typography,
    Box,
    Tooltip,
    CircularProgress,
    useTheme,
    Grid,
    Card,
    CardContent,
} from '@mui/material';
import {
    Edit as EditIcon,
    Visibility as ViewIcon,
    Add as AddIcon,
    FilterList as FilterIcon,
    AccountTree as SubOrderIcon,
    Clear as ClearIcon,
    PictureAsPdf as PdfIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { getServiceOrders } from '../../services/serviceOrders';
import LoadingScreen from '../LoadingScreen';
import PageContainer from '../PageContainer';
import { useAuth } from '../../contexts/AuthContext';
import usePagination from '../../hooks/usePagination';
import useSearch from '../../hooks/useSearch';
import SearchBar from '../common/SearchBar';
import KQSPLPagination from '../common/Pagination';
import { toast } from 'react-toastify';
import { PrimaryButton, SecondaryButton, DestructiveButton } from '../common/Buttons';
import CustomDropdown from '../common/CustomDropdown';
import { CustomTextField } from '../common/CustomTextFields';
import { formatDateTime, formatDateOnly, formatTimeOnly } from '../../utils/dateUtils';
import { getServiceOrderStatusChipColor, getServiceOrderPriorityChipColor, formatStatusLabel } from '../../constants/statusColors';
import { toastApiError } from '../../utils/errorHandler';

import { getSavedFilterState, clearSavedFilterState, hasActiveFilters } from '../../utils/filterStorage';
import { exportFilteredServiceOrdersPDF } from '../../utils/serviceOrderPdfExport';

const DEFAULT_FILTERS = {
    status: '',
    priority: '',
    is_sub_service_order: '',
    ordering: '-created_at',
    scheduled_at__gte: '',
    scheduled_at__lte: '',
    technician: '',
    company: ''
};

const ServiceOrderList = () => {
    const theme = useTheme();
    const { user } = useAuth();
    const navigate = useNavigate();

    const [appliedFilters, setAppliedFilters] = useState(() => getSavedFilterState('service_orders', DEFAULT_FILTERS).params);
    const [draftFilters, setDraftFilters] = useState(appliedFilters);
    const [showFilters, setShowFilters] = useState(() => hasActiveFilters(appliedFilters, DEFAULT_FILTERS));
    const [statusOptions, setStatusOptions] = useState([]);
    const [priorityOptions, setPriorityOptions] = useState([]);
    const [companyOptions, setCompanyOptions] = useState([]);
    const [technicianOptions, setTechnicianOptions] = useState([]);
    const [initialLoading, setInitialLoading] = useState(true);
    const [exportingPdf, setExportingPdf] = useState(false);

    const {
        data: orders,
        loading,
        error,
        pagination,
        handlePageChange,
        handlePageSizeChange,
        refresh,
        handleParamsChange
    } = usePagination(getServiceOrders, appliedFilters, 50, 'service_orders');

    // Check user permissions for service orders
    const canViewServiceOrders = user?.role?.permissions?.service_orders?.view;
    const canEditServiceOrders = user?.role?.permissions?.service_orders?.change;
    const canAddServiceOrders = user?.role?.permissions?.service_orders?.add;

    // Mark initial load as done once data arrives for the first time
    useEffect(() => {
        if (initialLoading && !loading) {
            setInitialLoading(false);
        }
    }, [loading, initialLoading]);

    // Organize orders hierarchically: parents first, then their children grouped below
    const organizedOrders = useMemo(() => {
        if (!orders || orders.length === 0) {
            return orders || [];
        }

        // Separate parents and sub-orders
        const parentOrders = orders.filter(o => !o.is_sub_service_order);
        const subOrders = orders.filter(o => o.is_sub_service_order);

        // Create a map of parent_id -> sub-orders
        const subOrdersByParent = {};
        subOrders.forEach(sub => {
            const parentId = sub.parent_order_id;
            if (parentId) {
                if (!subOrdersByParent[parentId]) {
                    subOrdersByParent[parentId] = [];
                }
                subOrdersByParent[parentId].push(sub);
            }
        });

        // Build the organized list: parent followed by its children
        const result = [];
        const processedSubOrderIds = new Set();

        parentOrders.forEach(parent => {
            // Add parent with metadata about children
            const children = subOrdersByParent[parent.id] || [];
            result.push({
                ...parent,
                _childCount: children.length,
                _isParentInGroup: children.length > 0
            });

            // Add children directly after parent
            children.forEach((child, index) => {
                result.push({
                    ...child,
                    _isChildInGroup: true,
                    _isLastChild: index === children.length - 1,
                    _childIndex: index
                });
                processedSubOrderIds.add(child.id);
            });
        });

        // Add any orphan sub-orders (whose parent is not in current page)
        subOrders.forEach(sub => {
            if (!processedSubOrderIds.has(sub.id)) {
                result.push({
                    ...sub,
                    _isOrphanChild: true
                });
            }
        });

        return result;
    }, [orders]);

    // Load filter options
    useEffect(() => {
        const loadFilterOptions = async () => {
            try {
                const { getServiceOrderStatuses, getServiceOrderPriorities } = await import('../../services/serviceOrders');
                const { getAllCompanies } = await import('../../services/companies');
                const { getUsersByRole } = await import('../../services/users');
                const [statuses, priorities, companiesRes, techniciansRes] = await Promise.all([
                    getServiceOrderStatuses(),
                    getServiceOrderPriorities(),
                    getAllCompanies(),
                    getUsersByRole('Technician')
                ]);
                setStatusOptions(statuses || []);
                setPriorityOptions(priorities || []);
                
                const comps = Array.isArray(companiesRes) ? companiesRes : (companiesRes?.results || []);
                setCompanyOptions(comps);
                
                let techs = Array.isArray(techniciansRes) ? techniciansRes : [];
                const isTechnician = (user?.role?.name || '').toLowerCase().includes('technician');
                if (isTechnician && user?.id) {
                    const userTech = techs.find(t => String(t.id) === String(user.id)) || {
                        id: user.id,
                        first_name: user.first_name || user.username || 'Me',
                        last_name: user.last_name || '',
                        username: user.username || ''
                    };
                    techs = [userTech];
                }
                setTechnicianOptions(techs);
            } catch (error) {
                console.error('Error loading filter options:', error);
                toastApiError(toast.error, error, 'Failed to load filter options', 'view filter options');
            }
        };
        loadFilterOptions();
    }, [user]);

    // Draft filter change (form only; API not called until Apply)
    const handleDraftFilterChange = (field, value) => {
        setDraftFilters((prev) => ({ ...prev, [field]: value }));
    };

    // Apply draft filters to API
    const applyFilters = () => {
        const next = { ...draftFilters, ordering: appliedFilters.ordering };
        if (searchQuery) next.search = searchQuery;
        setAppliedFilters(next);
        handleParamsChange(next);
    };

    // Sort applies immediately (separate from filters)
    const handleSortChange = (ordering) => {
        const next = { ...appliedFilters, ordering };
        setAppliedFilters(next);
        handleParamsChange(next);
    };

    // Clear all filters and sort to default
    const clearFilters = () => {
        clearSavedFilterState('service_orders');
        setDraftFilters(DEFAULT_FILTERS);
        setAppliedFilters(DEFAULT_FILTERS);
        resetQuery();
        handleParamsChange(DEFAULT_FILTERS);
    };

    // When opening filter panel, sync draft to applied so form shows current state
    const handleToggleFilters = () => {
        if (!showFilters) setDraftFilters(appliedFilters);
        setShowFilters((prev) => !prev);
    };

    // Search hook with SO- prefix stripping and appliedFilters state sync
    const getSOSearchParams = useCallback((query) => {
        let searchValue = query;
        if (searchValue && searchValue.toUpperCase().startsWith('SO-')) {
            searchValue = searchValue.slice(3);
        }
        const next = { ...appliedFilters, search: searchValue || undefined };
        if (!searchValue) delete next.search;
        setAppliedFilters(next);
        return next;
    }, [appliedFilters]);

    const { searchQuery, searchInputRef, handleSearchChange, handleClearSearch, resetQuery } = useSearch(
        handleParamsChange,
        { getSearchParams: getSOSearchParams }
    );

    if (initialLoading && loading) {
        return <LoadingScreen message="Loading service orders…" />;
    }

    if (error) {
        return (
            <PageContainer>
                <Typography color="error" align="center">
                    {error}
                </Typography>
            </PageContainer>
        );
    }

    let columns = [
        {
            id: 'id',
            label: 'ID',
        },
        {
            id: 'req_id',
            label: 'REQ ID',
        },
        {
            id: 'service_type',
            label: 'Service Type',
        },
        {
            id: 'company',
            label: 'Company',
        },
        {
            id: 'status',
            label: 'Status',
        },
        {
            id: 'priority',
            label: 'Priority',
        },
        {
            id: 'technician',
            label: 'Technician',
        },
        {
            id: 'scheduled',
            label: 'Scheduled',
        },
        {
            id: 'actions',
            label: 'Actions',
        }
    ];

    // Filter columns based on permissions, not hardcoded role
    if (!canViewServiceOrders) {
        columns = columns.filter((column) => column.id !== 'service_type' && column.id !== 'priority' && column.id !== 'scheduled');
    }

    // Render a single order row
    const renderOrderRow = (order) => {
        const isChildInGroup = order._isChildInGroup;
        const isOrphanChild = order._isOrphanChild;
        const isParentWithChildren = order._isParentInGroup;
        const childCount = order._childCount || 0;

        return (
            <TableRow
                key={order.id}
                onClick={() => canViewServiceOrders && navigate(`/service-orders/${order.display_name || order.id}`)}
                title={canViewServiceOrders ? `Click to view ${order.display_name || order.name || `SO-${order.id}`}` : ''}
                sx={{
                    cursor: canViewServiceOrders ? 'pointer' : 'default',
                    backgroundColor: isChildInGroup
                        ? `${theme.palette.primary.main}08`
                        : isOrphanChild
                            ? `${theme.palette.warning.main}08`
                            : 'inherit',
                    '&:hover': canViewServiceOrders ? { bgcolor: 'action.hover' } : {}
                }}
            >
                <TableCell sx={{ whiteSpace: 'nowrap', maxWidth: 200 }} title={order.display_name || order.name || `SO-${order.id}`}>
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        pl: isChildInGroup ? 3 : 0
                    }}>
                        {/* Visual connector for children in grouped view */}
                        {isChildInGroup && (
                            <Typography
                                variant="body2"
                                sx={{ color: theme.palette.text.disabled, mr: 0.5 }}
                            >
                                └
                            </Typography>
                        )}
                        {/* Sub-order icon - only show for orphan children (parent not on page) */}
                        {order.is_sub_service_order && isOrphanChild && (
                            <Tooltip title="Sub-order (parent not on this page)">
                                <SubOrderIcon
                                    color="warning"
                                    fontSize="small"
                                />
                            </Tooltip>
                        )}
                        {/* Parent indicator with child count */}
                        {isParentWithChildren && (
                            <Tooltip title={`${childCount} sub-order${childCount > 1 ? 's' : ''}`}>
                                <Chip
                                    size="small"
                                    label={childCount}
                                    color="primary"
                                    sx={{
                                        minWidth: 24,
                                        height: 20,
                                        fontSize: '0.7rem',
                                        '& .MuiChip-label': { px: 0.75 }
                                    }}
                                />
                            </Tooltip>
                        )}
                        <Typography variant="body2" noWrap sx={{ fontWeight: order.name ? 600 : 400 }}>
                            {order.display_name || order.name || `SO-${order.id}`}
                        </Typography>
                        {/* Show parent reference for orphan sub-orders */}
                        {isOrphanChild && order.parent_order_id && (
                            <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                                (Parent: SO-{order.parent_order_id})
                            </Typography>
                        )}
                    </Box>
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{order.req_id || '-'}</TableCell>
                {canViewServiceOrders && <TableCell sx={{ whiteSpace: 'nowrap' }}>{order.service_type?.name || '-'}</TableCell>}
                <TableCell sx={{ whiteSpace: 'nowrap', maxWidth: 180 }} title={order.company?.name}>
                    {order.company ? (
                        <Typography variant="body2" noWrap sx={{ fontWeight: 600 }}>
                            {order.company.name}
                        </Typography>
                    ) : '-'}
                </TableCell>
                <TableCell>
                    <Chip
                        label={formatStatusLabel(order.status?.name) || '-'}
                        color={getServiceOrderStatusChipColor(order.status?.name)}
                        size="small"
                    />
                </TableCell>
                {canViewServiceOrders && <TableCell>
                    <Chip
                        label={formatStatusLabel(order.priority?.name) || '-'}
                        color={getServiceOrderPriorityChipColor(order.priority?.name)}
                        size="small"
                    />
                </TableCell>}
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {order.technician
                        ? ((order.technician.first_name || order.technician.last_name)
                            ? `${order.technician.first_name || ''} ${order.technician.last_name || ''}`.trim()
                            : order.technician.email)
                        : 'Unassigned'}
                </TableCell>
                {canViewServiceOrders && <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {formatDateTime(order.scheduled_at)}
                </TableCell>}
                <TableCell onClick={(e) => e.stopPropagation()}>
                    <div style={{ display: 'flex', gap: '4px' }}>
                        {canViewServiceOrders && (
                            <Chip
                                icon={<ViewIcon sx={{ fontSize: 16 }} />}
                                label="View"
                                size="small"
                                onClick={() => navigate(`/service-orders/${order.display_name || order.id}`)}
                                clickable
                                color="primary"
                                variant="outlined"
                                sx={{ fontSize: '0.75rem' }}
                            />
                        )}
                        {canEditServiceOrders && (
                            <Chip
                                icon={<EditIcon sx={{ fontSize: 16 }} />}
                                label="Edit"
                                size="small"
                                onClick={() => navigate(`/service-orders/${order.display_name || order.id}/edit`)}
                                clickable
                                color="primary"
                                variant="outlined"
                                sx={{ fontSize: '0.75rem' }}
                            />
                        )}
                    </div>
                </TableCell>
            </TableRow>
        );
    };

    const isFilterActive = hasActiveFilters(appliedFilters, DEFAULT_FILTERS) || Boolean(searchQuery);

    const handleExportFilteredPdf = async () => {
        try {
            setExportingPdf(true);
            toast.info('Generating PDF export for filtered results...');
            const exportParams = {
                ...appliedFilters,
                search: searchQuery || '',
                page_size: 1000,
            };
            const res = await getServiceOrders(exportParams);
            const list = Array.isArray(res) ? res : (res?.results || []);
            if (list.length === 0) {
                toast.warning('No service orders match the active filters.');
                return;
            }
            exportFilteredServiceOrdersPDF(
                list,
                appliedFilters,
                { statusOptions, priorityOptions, companyOptions, technicianOptions },
                searchQuery || ''
            );
            toast.success(`Successfully exported ${list.length} filtered service order(s) to PDF.`);
        } catch (err) {
            console.error('Error exporting PDF:', err);
            toast.error('Failed to export PDF report. Please try again.');
        } finally {
            setExportingPdf(false);
        }
    };

    return (
        <PageContainer>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2, mb: 2 }}>
                <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
                    <SecondaryButton
                        startIcon={<FilterIcon />}
                        onClick={handleToggleFilters}
                    >
                        {showFilters ? 'Hide filters' : 'Show filters'}
                    </SecondaryButton>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" color="text.secondary">Sort:</Typography>
                        <CustomDropdown
                            size="small"
                            fullWidth={false}
                            value={appliedFilters.ordering}
                            onChange={(e) => handleSortChange(e.target.value)}
                            options={[
                                { value: '-created_at', label: 'Newest first' },
                                { value: 'created_at', label: 'Oldest first' },
                                { value: 'name', label: 'Name (A–Z)' },
                                { value: '-name', label: 'Name (Z–A)' },
                                { value: 'scheduled_at', label: 'Scheduled (asc)' },
                                { value: '-scheduled_at', label: 'Scheduled (desc)' },
                            ]}
                            sx={{ minWidth: 180 }}
                        />
                    </Box>
                </Box>
                {canAddServiceOrders && (
                    <PrimaryButton
                        startIcon={<AddIcon />}
                        onClick={() => navigate('/service-orders/new')}
                    >
                        New service order
                    </PrimaryButton>
                )}
            </Box>

            {/* Search bar */}
            <Box sx={{ mb: 2 }}>
                <SearchBar
                    value={searchQuery}
                    onChange={handleSearchChange}
                    onClear={handleClearSearch}
                    inputRef={searchInputRef}
                    placeholder="Search by ID, REQ ID, Company, Customer, Service Type…"
                    startAdornment={loading && searchQuery ? <CircularProgress size={16} color="inherit" /> : undefined}
                    sx={{
                        '& .MuiOutlinedInput-root': {
                            backgroundColor: 'background.paper',
                        }
                    }}
                />
            </Box>

            {showFilters && (
                <Card sx={{ mb: 3 }}>
                    <CardContent>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
                            <Box sx={{ minWidth: 180, flex: '1 1 180px', maxWidth: 250 }}>
                                <CustomDropdown
                                    label="Status"
                                    value={draftFilters.status}
                                    onChange={(e) => handleDraftFilterChange('status', e.target.value)}
                                    options={statusOptions}
                                    getOptionValue={(opt) => opt.id}
                                    getOptionLabel={(opt) => opt.name}
                                    emptyLabel="All"
                                    fullWidth
                                    size="small"
                                />
                            </Box>
                            <Box sx={{ minWidth: 180, flex: '1 1 180px', maxWidth: 250 }}>
                                <CustomDropdown
                                    label="Priority"
                                    value={draftFilters.priority}
                                    onChange={(e) => handleDraftFilterChange('priority', e.target.value)}
                                    options={priorityOptions}
                                    getOptionValue={(opt) => opt.id}
                                    getOptionLabel={(opt) => opt.name}
                                    emptyLabel="All"
                                    fullWidth
                                    size="small"
                                />
                            </Box>
                            <Box sx={{ minWidth: 180, flex: '1 1 180px', maxWidth: 250 }}>
                                <CustomDropdown
                                    label="Order type"
                                    value={draftFilters.is_sub_service_order}
                                    onChange={(e) => handleDraftFilterChange('is_sub_service_order', e.target.value)}
                                    options={[
                                        { value: '', label: 'All orders' },
                                        { value: 'false', label: 'Parent service orders' },
                                        { value: 'true', label: 'Sub-service orders' }
                                    ]}
                                    fullWidth
                                    size="small"
                                />
                            </Box>
                            <Box sx={{ minWidth: 180, flex: '1 1 180px', maxWidth: 250 }}>
                                <CustomDropdown
                                    label="Company"
                                    value={draftFilters.company}
                                    onChange={(e) => handleDraftFilterChange('company', e.target.value)}
                                    options={companyOptions}
                                    getOptionValue={(opt) => opt.id}
                                    getOptionLabel={(opt) => opt.name}
                                    emptyLabel="All Companies"
                                    fullWidth
                                    size="small"
                                />
                            </Box>
                            <Box sx={{ minWidth: 180, flex: '1 1 180px', maxWidth: 250 }}>
                                <CustomDropdown
                                    label="Technician"
                                    value={draftFilters.technician}
                                    onChange={(e) => handleDraftFilterChange('technician', e.target.value)}
                                    options={technicianOptions}
                                    getOptionValue={(opt) => opt.id}
                                    getOptionLabel={(opt) => `${opt.first_name || ''} ${opt.last_name || ''}`.trim() || opt.email}
                                    emptyLabel="All Technicians"
                                    fullWidth
                                    size="small"
                                />
                            </Box>
                            <Box sx={{ minWidth: 180, flex: '1 1 180px', maxWidth: 250 }}>
                                <CustomTextField
                                    label="Scheduled From"
                                    type="date"
                                    value={draftFilters.scheduled_at__gte}
                                    onChange={(e) => handleDraftFilterChange('scheduled_at__gte', e.target.value)}
                                    fullWidth
                                    size="small"
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Box>
                            <Box sx={{ minWidth: 180, flex: '1 1 180px', maxWidth: 250 }}>
                                <CustomTextField
                                    label="Scheduled To"
                                    type="date"
                                    value={draftFilters.scheduled_at__lte}
                                    onChange={(e) => handleDraftFilterChange('scheduled_at__lte', e.target.value)}
                                    fullWidth
                                    size="small"
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Box>
                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                <PrimaryButton onClick={applyFilters} size="medium">
                                    Apply filters
                                </PrimaryButton>
                                <DestructiveButton
                                    startIcon={<ClearIcon />}
                                    onClick={clearFilters}
                                    size="medium"
                                >
                                    Clear filters
                                </DestructiveButton>
                                {isFilterActive && (
                                    <SecondaryButton
                                        startIcon={exportingPdf ? <CircularProgress size={16} color="inherit" /> : undefined}
                                        onClick={handleExportFilteredPdf}
                                        disabled={exportingPdf}
                                        size="medium"
                                    >
                                        {exportingPdf ? 'Exporting…' : 'Export PDF'}
                                    </SecondaryButton>
                                )}
                            </Box>
                        </Box>
                    </CardContent>
                </Card>
            )}

            <TableContainer>
                <Table>
                    <TableHead>
                        <TableRow>
                            {columns.map((column) => (
                                <TableCell key={column.id}>{column.label}</TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {organizedOrders.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={8} align="center">
                                    No service orders found
                                </TableCell>
                            </TableRow>
                        ) : (
                            organizedOrders.map((order) => renderOrderRow(order))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Pagination */}
            {!loading && orders && orders.length > 0 && (
                <KQSPLPagination
                    count={pagination.count}
                    page={pagination.page}
                    pageSize={pagination.pageSize}
                    totalPages={pagination.totalPages}
                    onPageChange={handlePageChange}
                    onPageSizeChange={handlePageSizeChange}
                    onRefresh={refresh}
                    disabled={loading}
                />
            )}

        </PageContainer>
    );
};

export default ServiceOrderList;
